import { useState, useRef } from 'react'
import { useVaultEntries } from './hooks/useVaultEntries'
import { useAuth } from '../../hooks/useAuth'
import { getOrCreateSalt, deriveKey, encryptValue, decryptValue } from '../../lib/vaultCrypto'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'
import { cn } from '../../lib/utils'
import { Portal } from '../../components/Portal'

// ─── Password gate ────────────────────────────────────────────────────────────
const PasswordGate = ({ onUnlock }) => {
  const { user } = useAuth()
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState('')
  const [loading,  setLoading]  = useState(false)

  const unlock = async (e) => {
    e.preventDefault()
    if (!password) return
    setLoading(true)
    setError('')
    try {
      const salt = getOrCreateSalt(user.id)
      const key  = await deriveKey(password, salt)
      onUnlock(key)
    } catch {
      setError('Failed to derive key. Please try again.')
    } finally { setLoading(false) }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mb-5">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-text-primary mb-1">Secrets Vault</h2>
      <p className="text-sm text-text-muted text-center mb-6 max-w-xs">
        Enter your vault password to unlock. This password is used to encrypt your data locally — it is never sent to our servers.
      </p>

      <div className="w-full max-w-sm bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-xs text-amber-800">
        <strong>⚠️ Warning:</strong> If you lose your vault password or your account credentials, your vault data cannot be recovered — there is no reset option.
      </div>

      <form onSubmit={unlock} className="w-full max-w-sm space-y-3">
        <input
          type="password"
          autoFocus
          placeholder="Vault password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-base border border-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading || !password}>
          {loading ? <Spinner size="sm" /> : 'Unlock Vault'}
        </Button>
      </form>
    </div>
  )
}

// ─── Add entry form ───────────────────────────────────────────────────────────
const AddEntryModal = ({ cryptoKey, isAdmin, onAdd, onClose }) => {
  const [name,     setName]     = useState('')
  const [value,    setValue]    = useState('')
  const [isShared, setIsShared] = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState('')

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim() || !value.trim()) return
    setSaving(true)
    setError('')
    try {
      const encrypted_data = await encryptValue(cryptoKey, value.trim())
      await onAdd({ name: name.trim(), encrypted_data, is_shared: isShared })
      onClose()
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">New Secret</h2>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          <input type="text" placeholder="Name (e.g. WiFi Password)" value={name} onChange={(e) => setName(e.target.value)} autoFocus
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          <textarea placeholder="Secret value" value={value} onChange={(e) => setValue(e.target.value)} rows={3}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary" />

          {isAdmin && (
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div onClick={() => setIsShared((v) => !v)} className={cn('w-10 h-5.5 rounded-full transition-colors relative', isShared ? 'bg-primary' : 'bg-border')} style={{ height: 22 }}>
                <span className={cn('absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform', isShared ? 'translate-x-5' : 'translate-x-0.5')} />
              </div>
              <span className="text-sm text-text-primary">Share with household admins</span>
            </label>
          )}

          {error && <p className="text-sm text-rose-600">{error}</p>}

          <div className="flex gap-2">
            <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" className="flex-1" disabled={saving}>{saving ? <Spinner size="sm" /> : 'Save Secret'}</Button>
          </div>
        </div>
      </form>
    </div>
    </Portal>
  )
}

// ─── Entry card ───────────────────────────────────────────────────────────────
const EntryCard = ({ entry, cryptoKey, onDelete }) => {
  const [revealed,   setRevealed]   = useState('')
  const [revealing,  setRevealing]  = useState(false)
  const [error,      setError]      = useState('')
  const [confirmDel, setConfirmDel] = useState(false)

  const toggle = async () => {
    if (revealed) { setRevealed(''); return }
    setRevealing(true)
    setError('')
    try {
      const plain = await decryptValue(cryptoKey, entry.encrypted_data)
      setRevealed(plain)
    } catch {
      setError('Decryption failed — wrong vault password?')
    } finally { setRevealing(false) }
  }

  return (
    <div className="bg-surface rounded-xl border border-border p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm text-text-primary">{entry.name}</span>
        <div className="flex gap-2 items-center">
          {confirmDel ? (
            <>
              <button onClick={() => setConfirmDel(false)} className="text-xs text-text-muted">Cancel</button>
              <button onClick={() => onDelete(entry.id)} className="text-xs text-rose-500 font-semibold">Delete</button>
            </>
          ) : (
            <button onClick={() => setConfirmDel(true)} className="text-text-muted hover:text-rose-500">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {revealed ? (
        <div className="bg-background rounded-lg px-3 py-2 font-mono text-sm text-text-primary break-all">{revealed}</div>
      ) : (
        <div className="bg-background rounded-lg px-3 py-2 font-mono text-sm text-text-muted tracking-widest">••••••••••••</div>
      )}
      {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}

      <button onClick={toggle} disabled={revealing}
        className="mt-2 text-xs text-primary font-semibold flex items-center gap-1 hover:opacity-80">
        {revealing ? <Spinner size="sm" /> : revealed ? (
          <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>Hide</>
        ) : (
          <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>Reveal</>
        )}
      </button>
    </div>
  )
}

// ─── Vault contents ───────────────────────────────────────────────────────────
const VaultContents = ({ cryptoKey }) => {
  const { user, isAdmin }                               = useAuth()
  const { data: entries = [], isLoading, createEntry, deleteEntry } = useVaultEntries()
  const [showAdd, setShowAdd] = useState(false)

  const personal = entries.filter((e) => !e.is_shared && e.created_by === user?.id)
  const shared   = entries.filter((e) => e.is_shared)

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>

  const Section = ({ title, items }) => (
    <div className="mb-6">
      <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-text-muted">None</p>
      ) : (
        <div className="space-y-2">
          {items.map((e) => (
            <EntryCard key={e.id} entry={e} cryptoKey={cryptoKey} onDelete={deleteEntry} />
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-text-primary">Vault</h1>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
          New Secret
        </button>
      </div>

      <Section title="Personal" items={personal} />
      {isAdmin && <Section title="Shared (Admins)" items={shared} />}

      {showAdd && (
        <AddEntryModal cryptoKey={cryptoKey} isAdmin={isAdmin} onAdd={createEntry} onClose={() => setShowAdd(false)} />
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
export const VaultPage = () => {
  const [cryptoKey, setCryptoKey] = useState(null)

  if (!cryptoKey) return <PasswordGate onUnlock={setCryptoKey} />
  return <VaultContents cryptoKey={cryptoKey} />
}
